// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Pull requests list in Agents (JP 2026-11-02, design/pr-tool): every open pull request in
// the person's GitHub organisations and the ones merged this week, read by the API with their own
// GitHub sign-in (GET /konvo/agents/pulls/inbox). The rules (what needs them, the groups, the
// filters) are pure functions here so AgentPullInbox.test.ts covers them.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import AgentPulls, {pullKey} from '@app/features/konvo_agents/state/AgentPulls';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import type {
	AgentPullInbox,
	AgentPullInboxItem,
	AgentPullRef,
	AgentThreadSummary,
} from '@fluxer/schema/src/domains/konvo/Agents';
import {makeAutoObservable, runInAction} from 'mobx';

/** Why a pull request needs the person now. */
export type PullNeed = 'review' | 'failed' | 'changes' | 'conflicts' | 'ready';

/** An agent chat a pull request came from (the bridge links pull requests to threads). */
export interface PullChat {
	machineId: string;
	threadId: string;
	title: string;
	provider: string;
}

/**
 * What the person should do about it: review what they're asked to; on their own (or their agents')
 * pull requests, fix failing checks, answer change requests, clear conflicts, or merge once ready.
 * Drafts and everyone else's pull requests need nothing.
 */
export function pullNeed(item: AgentPullInboxItem, theirs: boolean): PullNeed | null {
	if (item.state !== 'open') return null;
	if (item.reviewRequested) return 'review';
	if (!(item.mine || theirs) || item.draft) return null;
	if (item.checks === 'failed') return 'failed';
	if (item.review === 'changes_requested') return 'changes';
	if (item.mergeState === 'dirty') return 'conflicts';
	if (item.checks !== 'running' && (item.mergeState === 'clean' || item.mergeState === 'has_hooks')) return 'ready';
	return null;
}

export type PullFilter = 'all' | 'needs';

/** Reviewer filter: the viewer is asked to review it, nobody is asked, or this login reviews it. */
export const PULL_ME = '@me';
export const PULL_NO_REVIEWER = '@none';
/** Author filter: an agent chat made it. */
export const PULL_AGENTS = '@agents';

/**
 * The list's filters (design/pr-look, JP 2026-11-02), remembered on this device: All or Needs you,
 * then reviewer, author and project (repository), each null for any.
 */
export interface PullFilters {
	filter: PullFilter;
	reviewer: string | null;
	author: string | null;
	repository: string | null;
}
export const DEFAULT_PULL_FILTERS: PullFilters = {filter: 'all', reviewer: null, author: null, repository: null};
export const PULL_FILTERS_STORAGE_KEY = 'konvo.agents.pull-filters';

const loginOrNull = (value: unknown): string | null =>
	typeof value === 'string' && /^(@me|@none|@agents|[A-Za-z0-9][A-Za-z0-9-]{0,38}(\[bot\])?)$/.test(value)
		? value
		: null;

export function parsePullFilters(raw: string | null): PullFilters {
	try {
		const value = JSON.parse(raw ?? '{}');
		const repository =
			typeof value?.repository === 'string' && /^[\w.-]+\/[\w.-]+$/.test(value.repository) ? value.repository : null;
		return {
			filter: value?.filter === 'needs' ? 'needs' : 'all',
			reviewer: loginOrNull(value?.reviewer),
			author: loginOrNull(value?.author),
			repository,
		};
	} catch {
		return {...DEFAULT_PULL_FILTERS};
	}
}

const same = (a: string | undefined, b: string) => !!a && a.toLowerCase() === b.toLowerCase();

/** Whether a pull request passes the reviewer, author and project filters. */
export function pullMatches(item: AgentPullInboxItem, chat: PullChat | null, filters: PullFilters): boolean {
	const {reviewer, author, repository} = filters;
	if (repository && !same(item.repository, repository)) return false;
	if (reviewer === PULL_ME) {
		if (!item.reviewRequested) return false;
	} else if (reviewer === PULL_NO_REVIEWER) {
		if (item.state !== 'open' || (item.reviewers?.length ?? 0) > 0) return false;
	} else if (reviewer && !(item.reviewers ?? []).some((r) => same(r.login, reviewer))) return false;
	if (author === PULL_ME) {
		if (!item.mine) return false;
	} else if (author === PULL_AGENTS) {
		if (!chat) return false;
	} else if (author && !same(item.author, author)) return false;
	return true;
}

/** The authors and reviewers in the list, most pull requests first, for the filter menus. */
export function pullPeople(items: ReadonlyArray<AgentPullInboxItem>): {
	authors: Array<{login: string; count: number}>;
	reviewers: Array<{login: string; count: number}>;
} {
	const tally = (logins: Array<string>) => {
		const counts = new Map<string, {login: string; count: number}>();
		for (const login of logins) {
			const entry = counts.get(login.toLowerCase()) ?? {login, count: 0};
			entry.count++;
			counts.set(login.toLowerCase(), entry);
		}
		return [...counts.values()].sort((a, b) => b.count - a.count || a.login.localeCompare(b.login));
	};
	return {
		authors: tally(items.flatMap((item) => (item.author ? [item.author] : []))),
		reviewers: tally(items.flatMap((item) => (item.reviewers ?? []).map((r) => r.login))),
	};
}

export interface PullRow {
	item: AgentPullInboxItem;
	need: PullNeed | null;
	chat: PullChat | null;
}

export interface PullGroups {
	needs: Array<PullRow>;
	open: Array<PullRow>;
	merged: Array<PullRow>;
}

const NEED_ORDER: Record<PullNeed, number> = {review: 0, failed: 1, changes: 2, conflicts: 3, ready: 4};

/** The words of a search, lower case; `#12` and `12` both find number 12. */
export function searchWords(query: string): Array<string> {
	return query
		.toLowerCase()
		.split(/\s+/)
		.map((word) => word.replace(/^#(?=\d)/, ''))
		.filter(Boolean);
}

/**
 * Whether a pull request has every word of a search (design/pr-repo, JP 2026-11-02) in its title,
 * number, repository, branches, author, reviewers, labels or agent chat.
 */
export function pullSearchMatches(
	item: AgentPullInboxItem,
	chat: PullChat | null,
	words: ReadonlyArray<string>,
): boolean {
	if (!words.length) return true;
	const text = [
		item.title,
		item.repository,
		item.head,
		item.base,
		item.author ?? '',
		...(item.reviewers ?? []).map((reviewer) => reviewer.login),
		...(item.labels ?? []),
		chat?.title ?? '',
	]
		.join('\n')
		.toLowerCase();
	const number = String(item.number);
	return words.every((word) => word === number || text.includes(word));
}

/** The list's three groups after the filters and search: needs you (most urgent first), open, merged this week. */
export function pullGroups(
	items: ReadonlyArray<AgentPullInboxItem>,
	chats: ReadonlyMap<string, PullChat>,
	filters: PullFilters,
	query = '',
): PullGroups {
	const groups: PullGroups = {needs: [], open: [], merged: []};
	const words = searchWords(query);
	for (const item of items) {
		const chat = chats.get(pullKey(item)) ?? null;
		if (!pullMatches(item, chat, filters) || !pullSearchMatches(item, chat, words)) continue;
		const need = pullNeed(item, chat !== null);
		if (filters.filter === 'needs' && !need) continue;
		const row = {item, need, chat};
		if (item.state === 'merged') groups.merged.push(row);
		else if (need) groups.needs.push(row);
		else groups.open.push(row);
	}
	const newest = (a: PullRow, b: PullRow) => b.item.updatedAt.localeCompare(a.item.updatedAt);
	groups.needs.sort((a, b) => NEED_ORDER[a.need!] - NEED_ORDER[b.need!] || newest(a, b));
	groups.open.sort(newest);
	groups.merged.sort((a, b) => (b.item.mergedAt ?? '').localeCompare(a.item.mergedAt ?? ''));
	return groups;
}

/** Each pull request the person's agent chats link to, by pullKey; the newest chat wins. */
export function pullChats(
	machines: ReadonlyArray<{id: string; threads: ReadonlyArray<AgentThreadSummary> | null}>,
): Map<string, PullChat> {
	const out = new Map<string, PullChat & {at: string}>();
	for (const machine of machines) {
		for (const thread of machine.threads ?? []) {
			for (const pull of thread.pullRequests ?? []) {
				const key = pullKey(pull);
				const seen = out.get(key);
				if (seen && seen.at >= thread.updatedAt) continue;
				out.set(key, {
					machineId: machine.id,
					threadId: thread.id,
					title: thread.title,
					provider: thread.provider,
					at: thread.updatedAt,
				});
			}
		}
	}
	return new Map([...out].map(([key, {at: _at, ...chat}]) => [key, chat]));
}

/** GitHub owners of the repositories the chats' pull requests are in: the API searches them too. */
export function chatOwners(chats: ReadonlyMap<string, PullChat>): Array<string> {
	const owners = new Set<string>();
	for (const key of chats.keys()) {
		const owner = key.split('/')[0];
		if (owner) owners.add(owner);
	}
	return [...owners].sort().slice(0, 5);
}

/**
 * The title as the list shows it (design/pr-look): without the ClickUp task ids agents put in front
 * (`#869fmkv5a Prepare…`, `[CU-869e3chu2] …`), which say nothing at a glance. Never empty.
 */
export function pullTitle(title: string): string {
	const clean = title
		.replace(/(^|\s)[[(]?(?:CU-)?#?86[0-9a-z]{7}[\])]?:?(?=\s|$)/gi, '$1')
		.replace(/^[\s:·-]+/, '')
		.replace(/\s{2,}/g, ' ')
		.trim();
	return clean || title;
}

/** A login's GitHub picture from the list, when it is one the app may load. */
export function pullAvatar(inbox: AgentPullInbox | null, login: string | undefined): string | null {
	if (!login || !inbox?.avatars) return null;
	const url = inbox.avatars[login] ?? null;
	return url && (/^https:\/\//.test(url) || url.startsWith('/') || url.startsWith('data:image/')) ? url : null;
}

/** The owner and name of `owner/name`. */
export function repoParts(repository: string): {owner: string; name: string} {
	const [owner = '', name = repository] = repository.split('/');
	return {owner, name};
}

/**
 * How the list names a repository (design/pr-repo): its name alone, as the owner's picture beside it
 * says whose it is; `owner/name` only when two owners in the list have a repository of that name.
 */
export function repoLabel(repository: string, items: ReadonlyArray<AgentPullInboxItem>): string {
	const {name} = repoParts(repository);
	const key = repository.toLowerCase();
	const clash = items.some(
		(item) =>
			item.repository.toLowerCase() !== key && repoParts(item.repository).name.toLowerCase() === name.toLowerCase(),
	);
	return clash ? repository : name;
}

/** Repositories whose name has every word of a search, for the search's project suggestions. */
export function searchRepositories(
	items: ReadonlyArray<AgentPullInboxItem>,
	query: string,
): Array<{repository: string; count: number}> {
	const words = searchWords(query);
	if (!words.length) return [];
	return inboxRepositories(items).filter(({repository}) =>
		words.every((word) => repository.toLowerCase().includes(word)),
	);
}

/** Repositories in the list, for the repository filter (most pull requests first). */
export function inboxRepositories(
	items: ReadonlyArray<AgentPullInboxItem>,
): Array<{repository: string; count: number}> {
	const counts = new Map<string, {repository: string; count: number}>();
	for (const item of items) {
		const key = item.repository.toLowerCase();
		const entry = counts.get(key) ?? {repository: item.repository, count: 0};
		entry.count++;
		counts.set(key, entry);
	}
	return [...counts.values()].sort((a, b) => b.count - a.count || a.repository.localeCompare(b.repository));
}

function readPullFilters(): PullFilters {
	try {
		return parsePullFilters(getProtectedLocalStorage()?.getItem(PULL_FILTERS_STORAGE_KEY) ?? null);
	} catch {
		return {...DEFAULT_PULL_FILTERS};
	}
}

class AgentPullInboxStore {
	inbox: AgentPullInbox | null = null;
	/** The last read failed (the list keeps what it had). */
	failed = false;
	loading = false;
	/** The filters, remembered on this device. */
	filters: PullFilters = readPullFilters();
	/** The search field is open (design/pr-repo); what it searches for. Not remembered. */
	searching = false;
	query = '';
	private lastAt = 0;
	/** The owners the last read asked for, so a live update reads the same list. */
	private owners: ReadonlyArray<string> = [];

	constructor() {
		makeAutoObservable<this, 'lastAt' | 'owners'>(this, {lastAt: false, owners: false}, {autoBind: true});
	}

	resetForTesting(): void {
		this.inbox = null;
		this.failed = false;
		this.loading = false;
		this.filters = {...DEFAULT_PULL_FILTERS};
		this.searching = false;
		this.query = '';
		this.lastAt = 0;
	}

	/** Opens or closes the search field; closing forgets the search. */
	setSearching(open: boolean): void {
		this.searching = open;
		if (!open) this.query = '';
	}

	setQuery(query: string): void {
		this.query = query;
	}

	/** Changes some filters and remembers them on this device. */
	setFilters(change: Partial<PullFilters>): void {
		this.filters = {...this.filters, ...change};
		try {
			getProtectedLocalStorage()?.setItem(PULL_FILTERS_STORAGE_KEY, JSON.stringify(this.filters));
		} catch {
			/* Blocked storage still keeps the filters for this session. */
		}
	}

	get filtered(): boolean {
		const {filter, reviewer, author, repository} = this.filters;
		return filter !== 'all' || !!reviewer || !!author || !!repository;
	}

	/** Reads the list (the API answers polls within 20 s from memory); `fresh` asks GitHub now. */
	async load(owners: ReadonlyArray<string>, opts: {fresh?: boolean; minAgeMs?: number} = {}): Promise<void> {
		const now = Date.now();
		if (!opts.fresh && now - this.lastAt < (opts.minAgeMs ?? 5_000)) return;
		this.lastAt = now;
		this.owners = owners;
		this.loading = true;
		try {
			const response = await http.get<AgentPullInbox>(Endpoints.KONVO_AGENTS_PULLS_INBOX, {
				query: {...(owners.length ? {owners: owners.join(',')} : {}), ...(opts.fresh ? {fresh: '1'} : {})},
				mode: 'silent',
			});
			runInAction(() => {
				this.loading = false;
				if (!response.ok) {
					this.failed = true;
					return;
				}
				this.failed = false;
				this.inbox = response.body;
			});
		} catch {
			runInAction(() => {
				this.loading = false;
				this.failed = true;
			});
		}
	}

	/**
	 * GitHub changed some of the person's pull requests (the API's watcher saw it, a 'pulls' Agents
	 * update): read the list again, and the ones open on screen. Only once the list was read here.
	 */
	refresh(changed: ReadonlyArray<string>): void {
		if (!this.inbox) return;
		void this.load(this.owners, {fresh: true});
		const keys = new Set(changed);
		const open = [...AgentPulls.pulls.values()].filter((pull) => keys.has(pullKey(pull)));
		if (open.length)
			void AgentPulls.load(
				open.map((pull) => ({repository: pull.repository, number: pull.number})),
				0,
			);
		for (const pull of open)
			if (AgentPulls.details.has(pullKey(pull))) void AgentPulls.loadDetails(pull).catch(() => undefined);
	}

	/** A row of the list by pull request, for the page's header before its own read arrives. */
	item(ref: AgentPullRef | null | undefined): AgentPullInboxItem | null {
		if (!ref) return null;
		const key = pullKey(ref);
		return this.inbox?.pulls.find((pull) => pullKey(pull) === key) ?? null;
	}
}

const store = new AgentPullInboxStore();
export default store;
